import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Box,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Link } from "react-router-dom";
import { fetchGateVisitorTypes } from "./Utils/gateVisitors.api";
import api, { API_BASE_URL } from "../../utils/api";
import GateVisitorDetailsModal from "./GateVisitorDetailsModal";
import { fetchGateVisitorStatuses } from "./Utils/gateVisitors.api";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import BadgeIcon from "@mui/icons-material/Badge";
import EventIcon from "@mui/icons-material/Event";
import ScheduleIcon from "@mui/icons-material/Schedule";
import GroupsIcon from "@mui/icons-material/Groups";



const GateVisitorsCards = ({ visits = [], getStatusDetails, onRefresh }) => {

  const [visitorTypes, setVisitorTypes] = useState([]);
  const [erroredImages, setErroredImages] = useState({});
  const [openImageModalId, setOpenImageModalId] = useState(null);
  const [membersMap, setMembersMap] = useState({});
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [visitorStatuses, setVisitorStatuses] = useState([]);


  useEffect(() => {
    const loadTypes = async () => {
      try {
        const response = await fetchGateVisitorTypes();
        const types = response?.data || response || [];
        setVisitorTypes(types);
      } catch (error) {
        console.error("Error fetching visitor types:", error);
      }
    };
    loadTypes();
  }, []);

  useEffect(() => {
    const fetchMember = async (clubMemberID) => {
      try {
        const res = await api.get(`/members/${encodeURIComponent(clubMemberID)}`);
        return res.data;
      } catch (err) {
        console.error(`Error loading member info for ID ${clubMemberID}:`, err);
        return null;
      }
    };

  const loadMembers = async () => {
    const ids = Array.from(
      new Set(visits.map((v) => v.ClubMemberID).filter(Boolean))
    );

    const map = {};
    await Promise.all(
      ids.map(async (id) => {
        const member = await fetchMember(id);
        if (member) map[id] = member;
      })
    );
    setMembersMap(map);
  };

  if (visits.length) loadMembers();
}, [visits]);

useEffect(() => {
  const loadDropdownData = async () => {
    try {
      const [typesRes, statusesRes] = await Promise.all([
        fetchGateVisitorTypes(),
        fetchGateVisitorStatuses()
      ]);
      const types = typesRes?.data || typesRes || [];
      const statuses = statusesRes?.data || statusesRes || [];
      setVisitorTypes(types);
      setVisitorStatuses(statuses);
    } catch (error) {
      console.error("Error loading dropdown data:", error);
    }
  };
  loadDropdownData();
}, []);


const handleEditChange = (field, value) => {
  setSelectedVisitor((prev) => ({
    ...prev,
    [field]: value,
  }));
};

const handleSaveVisitor = async () => {
  try {
    const payload = {
      ...selectedVisitor,
      AuthorizedBy: selectedVisitor.AuthorizedBy || "System", // or pull from user context
      EnteredBy: selectedVisitor.EnteredBy || "System",
    };

    await api.put(`/gate-visitors/${selectedVisitor.VisitID}`, payload);
    setSelectedVisitor(null);
  } catch (err) {
    console.error("Failed to save visitor", err);
  }
};


  const getVisitorTypeName = (typeId) => {
    const found = visitorTypes.find((t) => Number(t.TypeID) === Number(typeId));
    return found?.TypeName || "Unknown Type";
  };

  const handleImageError = (visitId) => {
    setErroredImages((prev) => ({ ...prev, [visitId]: true }));
  };

  if (!Array.isArray(visits) || visits.length === 0) {
    return (
      <Typography variant="body1" color="text.secondary" textAlign="center">
        No gate visitors found.
      </Typography>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2}>
        {visits.map((v) => {
          const statusName = v.StatusName || "Unknown";
          const { color = "grey", Icon = null, label = statusName } =
            getStatusDetails?.(statusName) || {};

          const initials = `${v.FirstName?.[0] || ""}${v.LastName?.[0] || ""}`.toUpperCase();
          const photoUrl = v.PicturePath
            ? `${API_BASE_URL}/gate-visitor-images/${v.PicturePath}`
            : null;
          const showInitials = !photoUrl || erroredImages[v.VisitID];

          const member = membersMap[String(v.ClubMemberID).trim()];

          return (
            <Grid item xs={12} sm={6} md={4} key={v.VisitID}>
         <Card
  sx={{
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    borderRadius: 3,
    boxShadow: 4,
    overflow: "hidden",
    height: "100%",
  }}
>
  {/* Visitor Image / Initials */}
  <Box
    sx={{
      width: 140,
      backgroundColor: "#f5f5f5",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: !showInitials ? "pointer" : "default",
    }}
    onClick={!showInitials ? () => setOpenImageModalId(v.VisitID) : undefined}
  >
    {showInitials ? (
      <Typography
        variant="h3"
        fontWeight="bold"
        color="#999"
        sx={{ textAlign: "center" }}
      >
        {initials}
      </Typography>
    ) : (
      <img
        src={photoUrl}
        alt="Visitor"
        onError={() => handleImageError(v.VisitID)}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
    )}
  </Box>

  {/* Card Content */}
  <CardContent sx={{ flex: 1, p: 2 }}>
    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
      Visit #{v.VisitID}
    </Typography>

    <Box display="flex" alignItems="center" mb={0.5}>
      <PersonIcon fontSize="small" sx={{ mr: 1 }} />
      <Typography variant="body2" noWrap>
        <strong>Visitor:</strong> {`${v.FirstName || ""} ${v.LastName || ""}`.trim()}
      </Typography>
    </Box>

    <Box display="flex" alignItems="center" mb={0.5}>
      <EventIcon fontSize="small" sx={{ mr: 1 }} />
      <Typography variant="body2">
        <strong>Arrival:</strong>{" "}
        {v.ArrivalDate ? new Date(v.ArrivalDate).toLocaleString() : "N/A"}
      </Typography>
    </Box>

    <Box display="flex" alignItems="center" mb={1}>
      <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
      <Typography variant="body2">
        <strong>Departure:</strong>{" "}
        {v.DepartureDate ? new Date(v.DepartureDate).toLocaleString() : "N/A"}
      </Typography>
    </Box>

    <Box display="flex" alignItems="center" mb={0.5}>
      <BadgeIcon fontSize="small" sx={{ mr: 1 }} />
      <Typography variant="body2">
        <strong>Member ID:</strong> {v.ClubMemberID}
      </Typography>
    </Box>

    {member ? (
      <>
        <Box display="flex" alignItems="center" mb={0.5}>
          <PersonIcon fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="body2" noWrap>
            <strong>Member:</strong> {member.FirstName} {member.LastName}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" mb={0.5}>
          <HomeIcon fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="body2" noWrap>
            <strong>Address:</strong> {member.FullAddress}
          </Typography>
        </Box>
      </>
    ) : (
      <Typography variant="body2">Member info not found.</Typography>
    )}

 

    <Box display="flex" alignItems="center" mb={1}>
      <GroupsIcon fontSize="small" sx={{ mr: 1 }} />
      <Chip
        label={getVisitorTypeName(v.VisitorTypeID)}
        size="small"
        sx={{
          backgroundColor: "#1976d2",
          color: "white",
          fontWeight: "bold",
        }}
      />
    </Box>

    <Chip
      icon={Icon ? <Icon sx={{ color: "#fff", fontSize: 18 }} /> : null}
      label={label.toUpperCase()}
      sx={{
        backgroundColor: (theme) => theme.palette[color]?.main || color,
        color: "#fff",
        fontWeight: "bold",
        mb: 1,
      }}
    />

   
<Button
  variant="contained"
  size="small"
  fullWidth
  sx={{ mt: 1 }}
  onClick={() => setSelectedVisitor(v)}
>
  View Details
</Button>

  </CardContent>

  {/* Image Modal */}
  <Dialog
    open={openImageModalId === v.VisitID}
    onClose={() => setOpenImageModalId(null)}
    maxWidth="md"
  >
    <DialogContent sx={{ position: "relative", textAlign: "center" }}>
      <IconButton
        onClick={() => setOpenImageModalId(null)}
        sx={{ position: "absolute", top: 8, right: 8 }}
      >
        <CloseIcon />
      </IconButton>
      <img
        src={photoUrl}
        alt="Visitor"
        style={{ width: "100%", maxHeight: "90vh", objectFit: "contain" }}
      />
    </DialogContent>
  </Dialog>
</Card>

<GateVisitorDetailsModal
  open={!!selectedVisitor}
  visitor={selectedVisitor}
  onClose={() => setSelectedVisitor(null)}
  onChange={handleEditChange}
  onSave={handleSaveVisitor}
  visitorTypes={visitorTypes}
  visitorStatuses={visitorStatuses}
  onAfterSave={onRefresh}

/>



          </Grid>
          
          );
        })}
      </Grid>
    </Box>
  );
};

export default GateVisitorsCards;
